import {singleton} from "tsyringe";
import {BehaviorSubject, distinctUntilChanged, type Observable} from "rxjs";

/**
 * Counts the board and row queries of the timeline views that are still open, so the views can
 * show a loading bar while entries are missing (ticket AaDIdeKTd4mzxpHJI8SoCA).
 *
 * Each query calls `begin()` and the returned `end()` once it settles — success, error or abort.
 * The returned function is one-shot: a second call ends nothing, so the count cannot drop below
 * the loads that are really open.
 */
@singleton()
export class TimelineLoadTracker {
    private openLoads = 0;
    private readonly busy = new BehaviorSubject<boolean>(false);

    /** True while at least one load is open; replays the current value to new subscribers. */
    public readonly busy$: Observable<boolean> = this.busy.pipe(distinctUntilChanged());

    /** Whether a load is open right now. */
    public get isBusy(): boolean {
        return this.openLoads > 0;
    }

    /**
     * Registers one open load.
     *
     * @returns the function that ends this load; calling it again does nothing
     */
    public begin(): () => void {
        this.openLoads++;
        this.busy.next(true);
        let ended = false;
        return () => {
            if (ended) {
                return;
            }
            ended = true;
            this.openLoads--;
            this.busy.next(this.openLoads > 0);
        };
    }
}
